import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { CanAccess, useDataProvider, useTranslate } from "ra-core";
import { Link, Navigate } from "react-router";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

import {
  ListCardsSkeleton,
  ListEmptyState,
  ListErrorState,
} from "../design/ListStates";
import { formatInstant } from "../guardias/guardiasShared";
import type { CrmDataProvider } from "../providers/types";
import type { WhatsAppUnclassifiedRow } from "../types";
import { ClassifyLeadDialog } from "./ClassifyLeadDialog";
import { MarkPersonalDialog } from "./MarkPersonalDialog";
import { formatSenderPhone } from "./unclassifiedRules";
import { UNREAD_CAP } from "./whatsappInboxRules";
import { WhatsAppThread } from "./WhatsAppThread";

/** Conversations loaded per step; 'Mostrar mas' adds another page. */
export const TRAY_PAGE_SIZE = 100;

/**
 * One unclassified conversation: the sender number and profile name (the tray is holder-only),
 * the known contact when there is one, the last message preview (an echo of what Pepe wrote from
 * the phone app is labelled 'Pepe (app)'), the unread count, the last activity, a lazily loaded
 * thread and the two actions. No call control anywhere.
 */
export const UnclassifiedRowCard = ({
  row,
}: {
  row: WhatsAppUnclassifiedRow;
}) => {
  const translate = useTranslate();
  const [leadOpen, setLeadOpen] = useState(false);
  const [personalOpen, setPersonalOpen] = useState(false);
  const unread =
    row.unreadCount > UNREAD_CAP ? `${UNREAD_CAP}+` : row.unreadCount;
  const lastAt = row.lastMessage?.at ?? row.lastMessageAt;
  const preview =
    row.lastMessage?.body?.trim() ||
    translate(
      row.lastMessage
        ? "crm.whatsapp.inbox.media_message"
        : "crm.whatsapp.inbox.no_messages",
    );
  return (
    <Card data-testid="whatsapp-unclassified-row">
      <CardContent className="space-y-2 p-4">
        <div className="flex flex-wrap items-center gap-2">
          <p className="min-w-0 flex-1 truncate text-base font-bold">
            {translate("crm.whatsapp.inbox.unmatched_name")}
          </p>
          {row.lastOrigin === "app" ? (
            <Badge variant="outline" className="text-sm">
              {translate("crm.whatsapp.unclassified.echo")}
            </Badge>
          ) : null}
          {row.unreadCount > 0 ? (
            <span className="text-sm font-medium tabular-nums">
              {translate("crm.whatsapp.badges.unread", { count: unread })}
            </span>
          ) : null}
        </div>
        {row.senderPhone || row.profileName ? (
          <p
            data-testid="whatsapp-unclassified-sender"
            className="text-base font-medium"
          >
            {[
              row.senderPhone ? formatSenderPhone(row.senderPhone) : null,
              row.profileName,
            ]
              .filter(Boolean)
              .join(" · ")}
          </p>
        ) : null}
        {row.knownContact ? (
          <p className="text-base">
            {translate("crm.whatsapp.unclassified.known_contact")}{" "}
            <Link
              className="font-medium underline"
              to={`/contacts/${row.knownContact.id}/show`}
            >
              {row.knownContact.name ??
                translate("crm.whatsapp.unclassified.known_contact_open")}
            </Link>
          </p>
        ) : null}
        <p className="truncate text-base text-muted-foreground">{preview}</p>
        {lastAt ? (
          <p className="text-[15px] text-muted-foreground">
            {translate("crm.whatsapp.unclassified.last_activity", {
              at: formatInstant(lastAt),
            })}
          </p>
        ) : null}
        <WhatsAppThread conversationId={row.conversationId} />
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            className="h-11"
            onClick={() => setLeadOpen(true)}
          >
            {translate("crm.whatsapp.unclassified.es_lead")}
          </Button>
          <Button
            type="button"
            variant="outline"
            className="h-11"
            onClick={() => setPersonalOpen(true)}
          >
            {translate("crm.whatsapp.unclassified.personal.action")}
          </Button>
        </div>
        <ClassifyLeadDialog
          conversationId={row.conversationId}
          knownContact={row.knownContact}
          open={leadOpen}
          onOpenChange={setLeadOpen}
        />
        <MarkPersonalDialog
          conversationId={row.conversationId}
          open={personalOpen}
          onOpenChange={setPersonalOpen}
        />
      </CardContent>
    </Card>
  );
};

const TrayList = () => {
  const translate = useTranslate();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const [limit, setLimit] = useState(TRAY_PAGE_SIZE);
  const tray = useQuery({
    queryKey: ["whatsapp", "unclassified", limit],
    queryFn: () => dataProvider.getWhatsAppUnclassified({ limit }),
    retry: false,
    placeholderData: (previous) => previous,
  });

  if (tray.error) {
    return (
      <ListErrorState
        message={translate("crm.whatsapp.unclassified.error")}
        onRetry={() => tray.refetch()}
      />
    );
  }
  if (!tray.data) return <ListCardsSkeleton rows={3} />;
  if (tray.data.rows.length === 0) {
    return (
      <ListEmptyState title={translate("crm.whatsapp.unclassified.empty")} />
    );
  }
  return (
    <div className="space-y-3">
      <ul className="space-y-3">
        {tray.data.rows.map((row) => (
          <li key={String(row.conversationId)}>
            <UnclassifiedRowCard row={row} />
          </li>
        ))}
      </ul>
      {tray.data.total > tray.data.rows.length ? (
        <Button
          type="button"
          variant="outline"
          className="h-11 w-full"
          disabled={tray.isFetching}
          onClick={() => setLimit(limit + TRAY_PAGE_SIZE)}
        >
          {translate("crm.whatsapp.unclassified.show_more")}
        </Button>
      ) : null}
    </div>
  );
};

/**
 * Almikia Epic EW3 (TASK-095): the 'Sin clasificar' tray. Only the holder of
 * `sales.can_classify_whatsapp_contacts` opens it (`canAccess` and the RLS agree); anyone else is
 * sent home. Lists the conversations that have no lead, each with 'Es lead' and 'Personal'.
 */
export const UnclassifiedTray = () => {
  const translate = useTranslate();
  return (
    <CanAccess
      resource="whatsapp_unclassified"
      action="list"
      accessDenied={<Navigate to="/" replace />}
    >
      <div className="mx-auto mt-2 max-w-3xl space-y-4 pb-8">
        <h1 className="px-1 text-2xl font-bold">
          {translate("crm.whatsapp.unclassified.title")}
        </h1>
        <p className="px-1 text-[15px] text-muted-foreground">
          {translate("crm.whatsapp.unclassified.intro")}
        </p>
        <Button asChild variant="outline" className="h-11">
          <Link to="/whatsapp_personal_numbers">
            {translate("crm.whatsapp.unclassified.numbers_link")}
          </Link>
        </Button>
        <TrayList />
      </div>
    </CanAccess>
  );
};

UnclassifiedTray.path = "/whatsapp_unclassified";
